import React, { useState } from 'react';
import Stack from '@mui/joy/Stack';
import Input from '@mui/joy/Input';
import Textarea from '@mui/joy/Textarea';
import Button from '@mui/joy/Button';
import Typography from '@mui/joy/Typography';
import api from '../../lib/api';
import { EmailTemplatePicker } from './EmailTemplatePicker';

interface EmailReplyComposerProps {
  threadId: number;
  defaultTo: string[];
  defaultCc?: string[];
  defaultSubject: string;
  /** Who the reply is going to — resolves the template's merge tags. */
  contactName?: string;
  onSent?: () => void;
  onCancel?: () => void;
}

const parseAddresses = (value: string): string[] =>
  value
    .split(/[,;]/)
    .map(a => a.trim())
    .filter(Boolean);

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    level="body-xs"
    sx={{ minWidth: 56, color: 'neutral.500', fontWeight: 'lg' }}
  >
    {children}
  </Typography>
);

/**
 * Email-style reply form (To / Cc / Subject / body) posting to
 * POST /api/inbox/email/{threadId}/reply/ — the reply is sent through the
 * requester's own connected Gmail/Outlook account and threads via
 * In-Reply-To/References on the backend.
 */
export const EmailReplyComposer: React.FC<EmailReplyComposerProps> = ({
  threadId,
  defaultTo,
  defaultCc = [],
  defaultSubject,
  contactName,
  onSent,
  onCancel,
}) => {
  const [to, setTo] = useState(defaultTo.join(', '));
  const [cc, setCc] = useState(defaultCc.join(', '));
  const [subject, setSubject] = useState(defaultSubject);
  const [bodyText, setBodyText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSend = bodyText.trim() !== '' && parseAddresses(to).length > 0;

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    setError(null);
    try {
      await api.post(`/api/inbox/email/${threadId}/reply/`, {
        to_addresses: parseAddresses(to),
        cc_addresses: parseAddresses(cc),
        subject: subject.trim() || undefined,
        body_text: bodyText,
        body_html: `<p>${bodyText.replace(/\n/g, '<br>')}</p>`,
      });
      setBodyText('');
      onSent?.();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  return (
    <Stack spacing={1}>
      <EmailTemplatePicker
        contact={{ full_name: contactName, email: defaultTo[0] }}
        onApply={(templateSubject, templateBody) => {
          // Fill the draft without clobbering it: anything already typed stays
          // and the template lands underneath.
          setSubject(prev => prev.trim() || templateSubject);
          setBodyText(prev => (prev.trim() ? `${prev.trimEnd()}\n\n${templateBody}` : templateBody));
        }}
      />
      <Input
        size="sm"
        startDecorator={<FieldLabel>To</FieldLabel>}
        placeholder="recipient@example.com"
        value={to}
        onChange={e => setTo(e.target.value)}
      />
      <Input
        size="sm"
        startDecorator={<FieldLabel>Cc</FieldLabel>}
        placeholder="Separate addresses with commas"
        value={cc}
        onChange={e => setCc(e.target.value)}
      />
      <Input
        size="sm"
        startDecorator={<FieldLabel>Subject</FieldLabel>}
        value={subject}
        onChange={e => setSubject(e.target.value)}
      />
      <Textarea
        placeholder="Type your reply…"
        value={bodyText}
        onChange={e => setBodyText(e.target.value)}
        minRows={4}
        maxRows={12}
        autoFocus
      />
      {error && (
        <Typography level="body-xs" sx={{ color: 'danger.500' }}>
          {error}
        </Typography>
      )}
      <Stack direction="row" spacing={1}>
        <Button
          size="sm"
          variant="solid"
          onClick={handleSend}
          disabled={!canSend || sending}
          loading={sending}
        >
          Send Reply
        </Button>
        {onCancel && (
          <Button size="sm" variant="plain" color="neutral" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </Stack>
    </Stack>
  );
};

export default EmailReplyComposer;
